import { logout } from 'tempus-okta-client';
import { matchPath } from 'react-router-dom';

import { Catalog, ChartMultitype, Hospital, NetworkAdminControl, SourceControl, HelpFilled } from '@carbon/icons-react';

import {
  NavigationButtonProps,
  NavigationButtonWithMenu,
  NavigationButtonWithoutMenu,
} from '@tcl-boron-prefabs/navigation-top';

import { creators as siteCreators } from '~/store/site';
import { getInitials } from '~/utils/misc';
import { getTrialsLandingPath } from '~/utils/toggleHelpers';
import paths, {
  BUILDER_NAV_TAB_STATE_KEY,
  BuilderLandingTab,
  getAllPatientsPageUrl,
  getSiteDocumentsSiteUrl,
  isAllSitesSiteId,
  matchesBuilderRoute,
  matchesProgramManagementRoute,
} from '~/routes/paths';
import {
  decodePatientWorkspaceOrigin,
  getPatientWorkspaceOriginTopNavSection,
  type PatientWorkspaceTopNavSection,
} from '~/routes/patientWorkspaceOrigin';

const TEMPUS_TOS_URL = 'https://www.tempus.com/providersponsor-termsofuse/';

const getSubdirectory = (path: string): string => path.split('/')[1];

/** Maps the current path to a site-scoped URL when impersonating external users. */
export const getPathForImpersonation = (pathname: string, siteId: string): string => {
  switch (getSubdirectory(pathname)) {
    case getSubdirectory(paths.PATIENT_TRACKER_SITES):
    case getSubdirectory(paths.TEMPUS_NURSE):
      return getAllPatientsPageUrl(siteId);
    case getSubdirectory(paths.SITE_DOCUMENTS):
      return getSiteDocumentsSiteUrl(siteId);
    default:
      return pathname;
  }
};

/** Which top-level nav group should read as active on the unified patient workspace. */
export type TopNavSection = PatientWorkspaceTopNavSection;

/** Nav group for `/patient/...`, from the same URL origin that drives the workspace breadcrumbs. */
export function getTopNavSectionForNavigation(input: { pathname: string; search: string }): TopNavSection | undefined {
  if (!matchesPatientV2Route(input.pathname)) {
    return undefined;
  }
  return getPatientWorkspaceOriginTopNavSection(decodePatientWorkspaceOrigin(input.search));
}

function matchesPathwayPatientsRoute(pathname: string): boolean {
  // Must not use `startsWith('/care-pathway')` alone — that matches `/care-pathways` (hub).
  if (pathname === '/care-pathway' || pathname.startsWith('/care-pathway/')) {
    return true;
  }
  return Boolean(matchPath({ path: paths.CARE_PATHWAY_QUEUE, end: false }, pathname));
}

function matchesCarePathwaysHubRoute(pathname: string): boolean {
  if (pathname === '/care-pathways') {
    return true;
  }

  const match = matchPath({ path: paths.CARE_PATHWAYS, end: true }, pathname);
  if (!match) {
    return false;
  }

  return isAllSitesSiteId(match.params.siteId);
}

function matchesPatientV2Route(pathname: string): boolean {
  return Boolean(matchPath({ path: paths.PATIENT_V2, end: false }, pathname));
}

function matchesTrialsRoute(pathname: string): boolean {
  return (
    pathname === paths.TRIAL_SEARCH ||
    pathname === paths.TRIAL_MATERIALS ||
    Boolean(matchPath(paths.TRIAL_DOCUMENTS, pathname)) ||
    Boolean(matchPath(paths.SITE_TRIAL, pathname))
  );
}

export const getAvatarConfig = (user, dispatch, navigate, canImpersonate: boolean) => {
  const primaryMenuItems = [
    ...(canImpersonate ? [{ text: 'View as a TIME site', id: 'impersonate' }] : []),
    { text: 'Notification Preferences', id: 'preferences' },
    { text: 'Terms of Use', id: 'terms-of-use' },
  ];

  return {
    initials: getInitials(user?.name) || '',
    name: user?.name || 'User',
    menu: {
      onChange: (selection) => {
        switch (selection) {
          case 'impersonate':
            dispatch(siteCreators.setImpersonationModalOpen(true));
            break;
          case 'preferences':
            navigate(paths.USER_NOTIFICATION_PREFERENCES);
            break;
          case 'terms-of-use':
            window.open(TEMPUS_TOS_URL, '_blank', 'noopener,noreferrer');
            break;
          case 'logout':
            logout();
            break;
        }
      },
      menuItems: [primaryMenuItems, [{ text: 'Logout', id: 'logout' }]],
    },
  };
};

export const getBaseButtonConfig = (
  permissions: {
    canViewInternalDocuments: boolean;
    canViewPatientTracking: boolean;
    canViewProviderReporting: boolean;
    canViewSiteDocuments: boolean;
  },
  urls: {
    matchReview: string;
    providerReporting: string;
    siteDocuments: string;
  },
  pathname: string,
  navigate: (path: string) => void,
  isMobile: boolean,
  topNavSection?: TopNavSection,
): NavigationButtonWithoutMenu[] => {
  return [
    {
      id: 'match-review',
      name: 'Trial Match Review',
      path: urls.matchReview,
      Icon: NetworkAdminControl,
      disabled: !permissions.canViewPatientTracking,
    },
    {
      id: 'reporting',
      name: 'Reporting',
      path: urls.providerReporting,
      Icon: ChartMultitype,
      disabled: !permissions.canViewProviderReporting,
    },
    {
      id: 'sites',
      name: 'Sites',
      path: urls.siteDocuments,
      Icon: Hospital,
      disabled: !permissions.canViewInternalDocuments,
    },
    {
      id: 'my-site',
      name: 'My Site',
      path: urls.siteDocuments,
      Icon: Hospital,
      disabled: !permissions.canViewSiteDocuments || permissions.canViewInternalDocuments,
    },
  ]
    .filter(({ disabled }) => !disabled)
    .map(({ id, name, path, Icon }) => {
      const onPath = pathname.startsWith(path);
      const matchReviewFromPatient =
        id === 'match-review' && topNavSection === 'match-review' && matchesPatientV2Route(pathname);
      const isActivePath = onPath || matchReviewFromPatient;
      return {
        id,
        onClick: () => navigate(path),
        buttonProps: {
          ariaLabel: name,
          text: name,
          Icon: isMobile ? Icon : undefined,
          type: isActivePath ? 'primary' : 'tertiary',
          isActive: isMobile && isActivePath,
        } as NavigationButtonProps,
      };
    });
};

export const getHelpButtonConfig = (): NavigationButtonWithMenu => {
  return {
    id: 'help',
    buttonProps: { ariaLabel: 'help', Icon: HelpFilled },
    menu: {
      onChange: (selection) => {
        if (selection === 'help-email-us') {
          window.open('mailto:trials@tempus.com', '_blank');
        }
      },
      menuItems: [[{ text: 'Email us at trials@tempus.com', id: 'help-email-us' }]],
    },
  };
};

export const getTrialsButtonConfig = (
  permissions: {
    canScreenPatients: boolean;
    canViewPatientTracking: boolean;
    isInternalPTUser: boolean;
    canViewCarePathways: boolean;
    canViewBuilder: boolean;
  },
  urls: {
    allPatients: string;
    screenedTrials: string;
    screenedPathways: string;
    tempusNurse: string;
    timePrescreening: string;
    cohortBuilderTrial: string;
  },
  pathname: string,
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  isMobile: boolean,
  topNavSection?: TopNavSection,
  builderNavTab?: BuilderLandingTab,
): NavigationButtonWithoutMenu | NavigationButtonWithMenu | null => {
  const items = [
    {
      id: 'trials',
      text: 'Trials and Materials',
      path: getTrialsLandingPath(),
      disabled: false,
    },
    {
      id: 'all-patients',
      text: 'All Patients',
      path: urls.allPatients,
      disabled: !permissions.canViewPatientTracking,
    },
    {
      id: 'tempus-nurse',
      text: 'Tempus Nurse',
      path: urls.tempusNurse,
      disabled: !permissions.canViewPatientTracking || !permissions.isInternalPTUser,
    },
    {
      id: 'screened-trials',
      text: 'Trials Pre-screening',
      path: urls.screenedTrials,
      disabled: !permissions.canScreenPatients,
    },
    {
      id: 'cohort-builder',
      text: 'Cohort Builder',
      path: urls.cohortBuilderTrial,
      disabled: !permissions.canViewBuilder,
    },
  ].filter(({ disabled }) => !disabled);

  const isPatientV2Page = matchesPatientV2Route(pathname);

  // Disable dynamic label for now, but isActive is still needed for the button state
  const [, isActive] = ((): [string, boolean] => {
    if (isPatientV2Page && topNavSection) {
      return ['Patients', topNavSection === 'trials'];
    }
    if (urls.timePrescreening === '/patient') {
      if (isPatientV2Page) {
        return ['TIME Prescreening', true];
      }
    } else if (pathname.startsWith(urls.timePrescreening)) {
      return ['TIME Prescreening', true];
    }

    if (matchesTrialsRoute(pathname)) {
      return ['Find Trials and Materials', true];
    }

    if (matchesBuilderRoute(pathname) && builderNavTab === 'trial') {
      return ['Cohort Builder', true];
    }

    switch (pathname) {
      case urls.allPatients:
        return ['All Patients', true];
      case urls.screenedTrials:
        return ['Trials Pre-screening', true];
      case urls.tempusNurse:
        return ['Tempus Nurse', true];
      default:
        return ['Patients', false];
    }
  })();

  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    const item = items[0];
    return {
      id: item.id,
      onClick: () => {
        if (item.id === 'cohort-builder') {
          navigate(item.path, { state: { [BUILDER_NAV_TAB_STATE_KEY]: 'trial' } });
        } else {
          navigate(item.path);
        }
      },
      buttonProps: {
        text: item.text,
        Icon: isMobile ? Catalog : undefined,
        type: isActive ? 'primary' : 'tertiary',
        isActive: isMobile && isActive,
        ariaLabel: item.text,
      } as NavigationButtonProps,
    };
  }

  return {
    id: 'trials',
    buttonProps: {
      text: 'Trials',
      Icon: isMobile ? Catalog : undefined,
      type: isActive ? 'primary' : 'tertiary',
      isActive: isMobile && isActive,
      ariaLabel: 'Patients',
    } as NavigationButtonProps,
    menu: {
      menuItems: [items],
      onChange: (v) => {
        const sel = items.find(({ id }) => id === v);
        if (sel) {
          if (sel.id === 'cohort-builder') {
            navigate(sel.path, { state: { [BUILDER_NAV_TAB_STATE_KEY]: 'trial' } });
          } else {
            navigate(sel.path);
          }
        }
      },
    },
  };
};

export const getPathwaysButtonConfig = (
  permissions: {
    canViewCarePathways: boolean;
    canScreenPatients: boolean;
    canViewBuilder: boolean;
    canViewProgramManagement: boolean;
  },
  urls: {
    carePathways: string;
    screenedPathways: string;
    cohortBuilder: string;
    programManagement: string;
  },
  pathname: string,
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  isMobile: boolean,
  topNavSection?: TopNavSection,
  builderNavTab?: BuilderLandingTab,
): NavigationButtonWithoutMenu | NavigationButtonWithMenu | null => {
  const items = [
    {
      id: 'care-pathways',
      text: 'Care Pathways',
      path: urls.carePathways,
      disabled: !permissions.canViewCarePathways,
    },
    {
      id: 'screened-pathways',
      text: 'Care Pathways Pre-screening',
      path: urls.screenedPathways,
      // The Pre-screening route is only registered for users who can view Care Pathways.
      disabled: !permissions.canScreenPatients || !permissions.canViewCarePathways,
    },
    {
      id: 'cohort-builder',
      text: 'Cohort Builder',
      path: urls.cohortBuilder,
      disabled: !permissions.canViewBuilder,
    },
    {
      id: 'program-management',
      text: 'Program Management',
      path: urls.programManagement,
      disabled: !permissions.canViewProgramManagement,
    },
  ].filter(({ disabled }) => !disabled);

  if (items.length === 0) {
    return null;
  }

  const [, isActive] = ((): [string, boolean] => {
    switch (pathname) {
      case urls.carePathways:
        return ['Care Pathways', true];
      case urls.screenedPathways:
        return ['Care Pathways Pre-screening', true];
      default:
        if (matchesBuilderRoute(pathname) && builderNavTab === 'care-pathway') {
          return ['Cohort Builder', true];
        }
        if (matchesCarePathwaysHubRoute(pathname)) {
          return ['Care Pathways', true];
        }
        if (matchesPathwayPatientsRoute(pathname)) {
          return ['Care Pathways', true];
        }
        if (matchesPatientV2Route(pathname) && topNavSection === 'care-pathways') {
          return ['Care Pathways', true];
        }
        if (matchesProgramManagementRoute(pathname)) {
          return ['Program Management', true];
        }
        return ['Care Pathways', false];
    }
  })();

  if (items.length === 1) {
    const item = items[0];
    return {
      id: item.id,
      onClick: () => {
        if (item.id === 'cohort-builder') {
          navigate(item.path, { state: { [BUILDER_NAV_TAB_STATE_KEY]: 'care-pathway' } });
        } else {
          navigate(item.path);
        }
      },
      buttonProps: {
        text: item.text,
        Icon: isMobile ? SourceControl : undefined,
        type: isActive ? 'primary' : 'tertiary',
        isActive: isMobile && isActive,
        ariaLabel: item.text,
      } as NavigationButtonProps,
    };
  }

  return {
    id: 'care-pathways',
    buttonProps: {
      text: 'Care Pathways',
      Icon: isMobile ? SourceControl : undefined,
      type: isActive ? 'primary' : 'tertiary',
      isActive: isMobile && isActive,
      ariaLabel: 'Care Pathways',
    } as NavigationButtonProps,
    menu: {
      menuItems: [items],
      onChange: (v) => {
        const sel = items.find(({ id }) => id === v);
        if (sel) {
          if (sel.id === 'cohort-builder') {
            navigate(sel.path, { state: { [BUILDER_NAV_TAB_STATE_KEY]: 'care-pathway' } });
          } else {
            navigate(sel.path);
          }
        }
      },
    },
  };
};

/**
 * Care Pathways nav group when the user can view Care Pathways or Program Management (Program Management is
 * feature-gated, so a user can have it without Care Pathways); otherwise standalone Cohort Builder, or nothing.
 */
export const getPathwaysOrBuilderButtonConfig = (
  permissions: Parameters<typeof getPathwaysButtonConfig>[0],
  urls: Parameters<typeof getPathwaysButtonConfig>[1],
  pathname: string,
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  isMobile: boolean,
  topNavSection?: TopNavSection,
  builderNavTab?: BuilderLandingTab,
): NavigationButtonWithoutMenu | NavigationButtonWithMenu | null => {
  if (permissions.canViewCarePathways || permissions.canViewProgramManagement) {
    return getPathwaysButtonConfig(permissions, urls, pathname, navigate, isMobile, topNavSection, builderNavTab);
  }
  return permissions.canViewBuilder
    ? getStandaloneCohortBuilderButtonConfig(urls, pathname, navigate, isMobile, builderNavTab)
    : null;
};

/** Cohort Builder entry when the Care Pathways nav group is hidden (user lacks patient-tracking pathway roles). */
export const getStandaloneCohortBuilderButtonConfig = (
  urls: { cohortBuilder: string },
  pathname: string,
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void,
  isMobile: boolean,
  builderNavTab?: BuilderLandingTab,
): NavigationButtonWithoutMenu => {
  const onBuilder = matchesBuilderRoute(pathname) && builderNavTab === 'care-pathway';
  return {
    id: 'cohort-builder',
    onClick: () => navigate(urls.cohortBuilder, { state: { [BUILDER_NAV_TAB_STATE_KEY]: 'care-pathway' } }),
    buttonProps: {
      text: 'Cohort Builder',
      Icon: isMobile ? SourceControl : undefined,
      type: onBuilder ? 'primary' : 'tertiary',
      isActive: isMobile && onBuilder,
      ariaLabel: 'Cohort Builder',
    } as NavigationButtonProps,
  };
};
